import React, { Suspense } from 'react';
import { Box, CircularProgress, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ClearAllPage } from '../views/ClearAllPage';
// T-08 (Batch 2 Phase 4): lazy-load analytics so echarts/markdown chunks only fetch
// when the user navigates there. Cuts ~600KB off the cold workspaces-page load.
// Cycle-4 LaTeX cutover (D5): KaTeX is NOT lazy-split — CSS imports at main.tsx,
// remark-math + rehype-katex bundle inline with the markdown stack so math
// renders on first paint everywhere MarkdownText mounts (no flicker).
// ♻️ THE DASHBOARD TAB RENDERS `DashboardPage`, NOT `AnalyticsPage` — the mock's `dash` screen.
// Operator: "all currently existing features except for dashboard get moved into the model
// homepage", and the mock's own subtitle for this screen is "Analytics is absorbed into this page —
// same marks, one changed axis: from stream to model."
// ⚠️ THE URL KEY STAYS `?view=analytics`, DELIBERATELY. It is the form `postLoginReturnTarget`
// writes and the one every existing deep link carries; renaming it would break those to rename an
// implementation detail the user never sees. `?view=dash` is accepted as an alias on READ so the
// mock's own coordinate resolves, but `analytics` remains the written form.
// 🔴 W-01 (2026-09-05) — THE SOURCE-SIDE HALF OF THE BOOT PAYLOAD, and it was one static import.
// `import { WorkspacePage, … } from '../views'` stood at the top of this file, so the entry graph
// reached WorkspaceCards -> EChartsFromUrl -> `echarts` and -> MarkdownText -> `react-markdown` /
// `rehype-katex`. `manualChunks` had already given those three their own FILES, and the config
// comment claimed that meant the cold load did not pay for them — but a chunk is not lazy because
// it is separate. Every one of them was `<link rel="modulepreload">`d and had to arrive AND
// evaluate before `createRoot().render()`, none of it needed to mount the auth gate.
// The default landing view is `{ type: 'models' }` (ModelListPage, already lazy), so nothing about
// a cold load needs this module at all.
// ⚠️ THIS EDIT ALONE DID NOT FIX IT, AND SAYING SO HERE IS THE POINT. Measured on the BUILT
// artifact after this change: `echarts` left the boot graph, `markdown-*.js` and `katex-*.js` did
// NOT — with no static source path from `main.tsx` to either. The remainder was a CHUNKING
// artifact (`react` had been emitted inside the chunk named `markdown`), and it is fixed in
// `vite.config.ts`, where the full measurement and both mechanisms are written down. Breaking the
// source edge was necessary and was not sufficient; a reader who trusts this comment alone will
// conclude the payload problem lives in this file, and it only half does.
// ⛔ The `../views` BARREL IS DELETED rather than left with two exports, because re-exporting a
// heavy surface from it is how this edge was created — its own comment said so — and a barrel with
// one remaining tenant is an invitation to add the next one.
// ⛔ Do NOT answer a slow boot by raising `BOOT_TIMEOUT_MS` in index.html. It does not touch the
// cause, and the payload is the cause. Guarded by `apps/test-scripts/frontend/boot/bootCriticalGraph.test.ts`,
// which asserts over the BUILT dist/index.html — a source-level check cannot see modulepreload.
const WorkspacePage = React.lazy(() => import('../views/WorkspacePage').then(m => ({ default: m.WorkspacePage })));
const DashboardPage = React.lazy(() => import('../views/model/DashboardPage').then(m => ({ default: m.DashboardPage })));
// WI-24 — the model homepage. Lazy for the same reason as the surfaces above: it is a distinct
// top-level destination, not part of the cold Projects load.
const ModelHomePage = React.lazy(() => import('../views/model/ModelHomePage').then(m => ({ default: m.ModelHomePage })));
const ModelListPage = React.lazy(() => import('../views/model/ModelListPage').then(m => ({ default: m.ModelListPage })));
const ModelRegisterPage = React.lazy(() => import('../views/model/ModelRegisterPage').then(m => ({ default: m.ModelRegisterPage })));
// ⛔ NOT a route gate any more. The `?view=reggie` ROUTE was full-deleted with `ReggieSurface`
// (2026-08-22, the overhaul shell); what survives is the LICENCE gate, and its one live consumer is
// the model homepage's `Test Ground` tab, which is where the surface now lives. The shell's rail
// renders no Reggie entry in either state. The hook is imported HERE rather than at the tab so one
// session-scoped read serves the whole app.
import {
  PhoneHeader, PhoneTabBar, PHONE_TAB_BAR_HEIGHT, ProjectTrailRow, SideRail,
  type PhoneTab, type PrdTopNav, type RailModelContext, type TrailStep,
} from '../components/layout';
import { NAV_ITEMS, TAB_ICON } from '../components/layout/navItems';
import { HolidayFrame, HolidayProvider } from '../theme/holiday';
import { getModel } from '../views/model/modelApi';
import { resolveTab, TAB_LABEL_KEY, visibleModelTabs } from '../views/model/modelTabs';
import { ErrorBoundary } from '../components/common/ErrorBoundary';
import type { Workspace } from '../app/contracts/domain';
import { useIdentity } from '../app/auth/sessionIdentity';
// ⚠️ `UrlBearingView` is no longer imported here. It was only ever named by `fromUrlView`'s
// signature, and that function is gone (see the note above `PrdApp`). The two unions are still
// required to agree — `dispatchLadderParity.test.ts` harvests both from SOURCE, so the check does
// not depend on this file importing the type.
import { backViewFromModelHome, backViewFromTakeover, resolveViewFromUrl, urlForView } from './viewFromUrl';

type PrdView =
  // WI-24 — the MODEL HOMEPAGE, the surface the model registry hangs off. URL-addressable as
  // `?view=model&model=mdl_…`; the id is REQUIRED because a model homepage without a model has
  // nothing to render, which is why the read ladder refuses the id-less form rather than
  // resolving to a blank page.
  // `create` / `exportJob`: the `W-14` TOOLBOX FRONT DOOR hand-off, consumed once at the mount hop
  // below and stripped from the URL there — the same treatment `?afterImport=1` has always had. See
  // `UrlBearingView`'s note in ./viewFromUrl for why the intent travels in the URL at all and why
  // that does not contradict `pendingCreate`'s "not a navigation coordinate" rule.
  | { type: 'model'; modelId: string; tab?: string; create?: string; exportJob?: string }
  // WI-49 — THE REAL MODEL LIST, reading `GET /api/models`.
  //
  // ⛔ `model_inventory` IS GONE (WI-37, 2026-08-22). It was a 9-fixture MOCK — the operator:
  // "the currently live model inventory is a mock not real" — and it is FULL-DELETED, page,
  // fixture module, route member and path allowlist entry together. `?view=model_inventory`
  // now resolves to the default Projects view like any other unrecognised key.
  | { type: 'models' }
  // Registration is a full SCREEN in the mock, not a dialog over the list.
  | { type: 'register' }
  // `autoOpenEnrichment`: transient flag set by the toolbox-export landing
  // (`?afterImport=1`). Forwarded to WorkspacePage so it can fire the
  // enrichment dialog once hydration completes. PrdApp consumes the URL
  // param + clears it via replaceState before mount so a refresh does
  // not re-trigger the dialog.
  // `originModelId`: the model this takeover was entered FROM, when it was — see `urlForView`.
  // It is what makes the back control return to the model's Projects tab instead of dumping the
  // user on the legacy flat list, and it is URL-borne so Back and a refresh recover it too.
  | { type: 'workspace'; workspaceId: string; workspace?: Workspace; autoOpenEnrichment?: boolean; originModelId?: string }
  | { type: 'clear' }
  // ⚠️ AMENDED 2026-08-29 — operator ruling R-Q15 (FULL REMOVAL of the magic-link plane, every env).
  // An `access-expired` member closed this union: "Phase 5: terminal page for
  // invalid/expired/revoked magic-link access. Reachable via /?view=access-expired&reason=<code>.
  // AppAuthGate also detects this URL and renders AccessExpiredPage *before* PrdApp mounts (since the
  // user has no access). This route exists so a logged-in admin landing on the URL still sees the
  // same page." Both that page and the gate branch are FULL-DELETED, so the member goes with them —
  // together with its `NAV_BY_VIEW` row and its render entry, which the
  // `Record<PrdView['type'], PrdTopNav>` TOTAL map makes a compile error to forget, and its two
  // `UrlBearingView` ladder arms in ./viewFromUrl.
  //
  // ⛔ `toolbox` JOINED THEM 2026-09-08 — OPERATOR RULING `R-C3`, and this one is a CLOSED HOLE
  // rather than a tidy-up. It was the STANDALONE Toolbox route (`?view=toolbox` / `/toolbox`), a
  // mount with no model in scope. `R-C3` makes every Toolbox run model-scoped: all five
  // `POST /api/toolbox/jobs/*` routes now require a `model_id` the caller is an owner or
  // collaborator on. A mount that has no model to name can only produce `model_id_required` 400s,
  // so keeping it would ship a permanently broken screen AND a second door onto a gated surface.
  // The model tab (`ModelHomePage`) is the surviving entry point and always names its model.
  | { type: 'analytics' };

// ⛔ `fromUrlView` IS GONE (2026-08-22). It existed for ONE reason — narrowing reggie's `?stage=`
// string to the `ReggieStage` enum on the way out of the pure resolver — and the `?view=reggie`
// route it served is full-deleted. With that arm removed it was `(v) => v`: an identity function,
// i.e. a shim, which §8's "no placeholder shims" forbids. `UrlBearingView` is directly assignable
// to `PrdView` (the two unions are member-for-member identical, asserted by
// `dispatchLadderParity.test.ts`; `PrdView` is only RICHER, on `workspace`), so both effects now
// pass the resolver's result to `setView` unwrapped.
export function PrdApp() {
  // 🔴 THE LANDING IS THE MODEL LIST (operator ruling 2026-08-23). This seeded the LEGACY flat
  // project list — a screen the concept doc draws as the BEFORE state and that the header nav
  // does not even link to. It is only the pre-URL-read seed (the effect below immediately
  // replaces it from `viewFromUrl`), but a seed that disagrees with the read ladder paints one
  // frame of the wrong page on every cold load.
  const [view, setView] = React.useState<PrdView>({ type: 'models' });
  // The project on screen, from its PERSISTED record (reported up by `WorkspacePage` once loaded): its
  // name for the trail and the registry model it hangs off (`workspaces.model_id`). Keyed by id so a
  // record from the previous project is never shown for the next one while it loads.
  const [project, setProject] = React.useState<{ workspaceId: string; name: string; modelId?: string } | null>(null);
  // That model's name, read through the model API (persist-then-render). Keyed by model id, as above.
  const [projectModel, setProjectModel] = React.useState<{ modelId: string; name: string } | null>(null);
  // The name of the model the page on screen has loaded, reported up by `ModelHomePage` so the rail can show it.
  const [modelName, setModelName] = React.useState<string | null>(null);
  // The published identity, read from GET /api/me by the ONE session exchange in AppAuthGate
  // (persist-then-render; components subscribe, never fetch). Reactivity comes free via
  // useSyncExternalStore. 
  //
  // ⚠️ AMENDED 2026-08-29 — operator ruling R-Q15. This block was headed "Admin gate for the
  // settings-menu Magic Link Manager row", and it bound `const isAdmin = identity.isAdmin` for the
  // single purpose of gating `onOpenAccessGrants`. That row, its dialog and the whole magic-link
  // plane are FULL-DELETED, so the binding lost its last consumer in this file and is removed rather
  // than left dead. `identity.isAdmin` itself is UNAFFECTED and still consumed by SideRail's account block,
  // ModelRegisterPage and ModelAccessTab.
  //
  // The block's other half is HISTORY WORTH KEEPING and is preserved verbatim, because it records a
  // trust-surface defect rather than a feature: "WI-32 (2026-08-20): this read the URL — `?mode=admin`,
  // via a `readIsAdminFromUrl` helper kept reactive by `popstate` plus a custom `urlchange` event
  // AnalyticsPage dispatched after its mode-toggle `replaceState`. All of that is FULL-DELETED. A
  // capability claim the CLIENT asserts is not a capability check: it was one URL edit wide, and
  // because it looked like a gate it was the most likely source of the belief that role-based FE
  // gating already existed." (Its closing sentence — that the two readers disagreed because
  // AnalyticsPage coerced a magic-link customer back to executive — is now moot for the same reason
  // this edit exists.) The standing rule it leaves behind: `isAdmin` is a UI signal only; the backend
  // admin endpoints are the enforcement point and 401 independently.
  const identity = useIdentity();
  const { t } = useTranslation();
  const muiTheme = useTheme();

  // WI-24 criterion 1 — the view -> URL ladder MOVED to `urlForView` in ./viewFromUrl, beside the
  // URL -> view reader it has to agree with. It was a `useCallback` body, which meant the write half
  // of the router contract could not be tested at all; the criterion requires coverage of BOTH
  // ladders AND this writer. Behaviour is unchanged — the lift was byte-faithful — and what stays
  // here is the only part that is not a pure function of the URL: the history push itself.
  const setUrlView = React.useCallback((next: PrdView) => {
    window.history.pushState({}, '', urlForView(next, window.location.href));
  }, []);

  // Synchronize view state when user presses browser back/forward
  React.useEffect(() => {
    const handlePopState = () => {
      // ONE read ladder, shared with the mount effect below — see `resolveViewFromUrl`'s note. The
      // inline copy that used to live here is what the module header meant by "two copies of this
      // ladder is how a fresh load and a Back button came to disagree".
      setView(resolveViewFromUrl(new URLSearchParams(window.location.search)));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Allow direct linking to a workspace for quick UI review:
  // - /?workspaceId=ws_acme
  // - /?workspaceId=ws_var
  // - /?view=clear (clear all data page)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resolved = resolveViewFromUrl(params);

    // The ONE thing the mount path does that `popstate` does not, and it is deliberately NOT in the
    // resolver: `?afterImport=1` is a TRANSIENT hint from the toolbox-export landing. It is consumed
    // once and stripped via replaceState (no history entry) so a refresh re-enters the workspace
    // without re-firing the enrichment dialog. A pure URL→view function cannot own a side effect,
    // and folding it in would have made the resolver untestable — which is the whole point of it.
    if (resolved.type === 'workspace' && params.get('afterImport') === '1') {
      const cleaned = new URL(window.location.href);
      cleaned.searchParams.delete('afterImport');
      window.history.replaceState({}, '', cleaned.toString());
      // ⚠️ `originModelId` is FORWARDED, not re-derived. This branch rebuilds the view by hand
      // instead of taking the resolver's, so every URL-borne field has to be carried explicitly —
      // dropping one here would silently strand the back control on exactly the entry path
      // (toolbox export → workspace) where the user is least likely to know where they came from.
      setView({
        type: 'workspace',
        workspaceId: resolved.workspaceId,
        autoOpenEnrichment: true,
        originModelId: resolved.originModelId,
      });
      return;
    }

    // ➕ 🔴 `W-14` step 2 — THE SECOND TRANSIENT HOP, and it is deliberately the SAME SHAPE as the
    // one above rather than a new mechanism. `?create=` / `?exportJob=` are the Toolbox export
    // modal's hand-off: open the create dialog on this defence line, and park that job's bundle into
    // whatever it produces. Both are consumed ONCE and stripped via `replaceState` (no history
    // entry), so Back and a refresh cannot re-arm a dialog the user already dealt with — the exact
    // property `ModelHomePage`'s `pendingCreate` docblock promises and the reason that comment's
    // "not in the URL, deliberately" is still true of the INTENT-AS-A-COORDINATE it is about.
    //
    // ⚠️ Like the hop above, this branch REBUILDS the view by hand, so every URL-borne field of the
    // model variant has to be listed explicitly — `tab` included. Dropping it here would land the
    // user on Overview with a create dialog armed for a tab they are not on.
    if (resolved.type === 'model' && (resolved.create || resolved.exportJob)) {
      const cleaned = new URL(window.location.href);
      cleaned.searchParams.delete('create');
      cleaned.searchParams.delete('exportJob');
      window.history.replaceState({}, '', cleaned.toString());
      setView({
        type: 'model',
        modelId: resolved.modelId,
        tab: resolved.tab,
        create: resolved.create,
        exportJob: resolved.exportJob,
      });
      return;
    }
    setView(resolved);
  }, []);

  // 🔴 FOUND BY A LIVE WALK, 2026-08-23, AND IT IS THE EXACT CLASS `viewFromUrl.ts` EXISTS FOR.
  // This was a ternary chain ending `: 'workspaces'`, so the `model` view — which is not a tab —
  // fell through to the CATCH-ALL and lit the **Projects** tab while the address bar said
  // `?view=model`. The router module's own header names this failure: it left "the address bar
  // saying one thing and the page (plus the nav highlight, which is derived from the view) saying
  // another." The chain reproduced it one layer up, for the same reason: a default that absorbs
  // every unlisted case cannot tell "belongs here" from "nobody decided".
  //
  // ⇒ A TOTAL MAP, NOT A DEFAULT. `Record<PrdView['type'], PrdTopNav>` makes TypeScript refuse a
  // missing key, so adding a view type is a COMPILE ERROR until someone states where it belongs.
  // That is stronger than a test, because it cannot be forgotten and cannot be skipped.
  // ⚠️ Several views deliberately map to a tab that is not "theirs" — `workspace` and `clear` both
  // sit under Projects. That is a DECISION, and writing it out is the point; the old chain reached
  // the same answer for three of them by accident and for `model` wrongly. (⚠️ 2026-08-29, R-Q15:
  // `access-expired` was the third view named here and is FULL-DELETED with the magic-link plane.)
  // ⚠️ RE-AIMED BY THE OVERHAUL SHELL, 2026-08-23. The nav is now `Models · Dashboard`, so most of
  // these values name a tab that no longer exists and therefore light nothing. That is CORRECT for
  // the terminal pages and WRONG for anything inside the Models section, so each is restated with
  // its reason rather than left to inherit the old answer — which is the defect this map replaced.
  const NAV_BY_VIEW: Record<PrdView['type'], PrdTopNav> = {
    models: 'models',               // the list — the shell's first tab
    register: 'models',             // registration is reached FROM the list and stays in its section
    model: 'models',                // a model homepage IS inside the Models section
    workspace: 'models',            // a project takeover is reached FROM a model
    analytics: 'analytics',         // Dashboard — the shell's second tab absorbs Analytics
    clear: 'clear',                 // destructive terminal page — lights nothing, deliberately
  };
  const nav: PrdTopNav = NAV_BY_VIEW[view.type];

  // ➕ 2026-10-26 — THE REGGIE CAPABILITY GATE IS FULL-DELETED. Operator ruling: Test Ground is
  // READ-visible to every tenant user and write is a per-model grant on the Access tab, so the
  // tri-state (the capabilities read AND the Reggie group flag on `/api/me`, both now deleted) no longer decides whether
  // the tab or its panel exists. ~~WI-09 criterion 3 — "a caller outside the Reggie group sees no
  // Test Ground tab, and a direct URL fails closed"~~ is amended in the registry beside the
  // original. Write denial is rendered from the persisted verdicts (`caller.may_write`, each row's
  // `can_write`), never from a client-side licence read.

  // ⛔ `handleOpenModelInventory` WAS HERE AND IS FULL-DELETED (2026-08-22), with the
  // `Model inventory` header button it drove — WI-37 criterion 1, unblocked now that `ModelListPage`
  // is the real list.
  //
  // Its docblock claimed the button was "the ONLY navigation path to the model plane in the
  // product". ⚠️ THAT STOPPED BEING TRUE WHEN THE TWO-TAB SHELL LANDED and the claim outlived it:
  // `onNavChange('models')` below dispatches `{ type: 'models' }` — the SAME view, from the header's
  // own `Models` tab. So the button was a second control for one destination, drawn where the locked
  // mock draws none, and outside the `!props.subtitle` guard that suppresses nav under the R-E
  // tier-1 takeover. Deleting it strands nothing; the tab is the path.

  // Stabilize callback reference to prevent infinite re-renders in WorkspacePage
  const handleWorkspaceLoad = React.useCallback((ws: Workspace) => {
    setProject({ workspaceId: ws.id, name: ws.name, modelId: ws.modelId });
  }, []);

  const currentProject = view.type === 'workspace' && project?.workspaceId === view.workspaceId ? project : null;
  // ⚠️ A project with no model (`workspaces.model_id` NULL on legacy rows, per the `Workspace` contract)
  // has no model level: the rail shows no model group and the trail goes Models › project. Nothing
  // is invented in its place.
  const projectModelId = currentProject?.modelId;
  React.useEffect(() => {
    if (!projectModelId) return undefined;
    let cancelled = false;
    getModel(projectModelId)
      .then((d) => { if (!cancelled) setProjectModel({ modelId: projectModelId, name: d.model.name }); })
      .catch((e: unknown) => {
        // Fail loud. The model step and the rail caption stay absent rather than showing a stand-in name.
        if (!cancelled) console.error('PrdApp: failed to load the project\'s model for the rail and trail', { modelId: projectModelId, error: e });
      });
    return () => { cancelled = true; };
  }, [projectModelId]);
  const projectModelName = projectModel && projectModel.modelId === projectModelId ? projectModel.name : null;

  // The ONE way a model tab is opened, shared by the page's tab strip and the rail's model links so
  // the two cannot write different URLs for the same click. Drops `create` and `exportJob`: they are
  // one-shot hand-offs for the tab they arrived on.
  const openModelTab = (modelId: string, tab: string) => {
    const next: PrdView = { type: 'model', modelId, tab };
    setUrlView(next);
    setView(next);
  };

  const go = (next: PrdView) => {
    setUrlView(next);
    setView(next);
  };

  // The rail's model level: on a model page, and on a project that hangs off a model. Inside a project
  // the active link is the tab a project lives under (`backViewFromTakeover`'s return tab).
  const takeoverReturn = projectModelId ? backViewFromTakeover(projectModelId) : null;
  const modelContext: RailModelContext | undefined = view.type === 'model'
    ? {
      name: modelName,
      tab: resolveTab(view.tab),
      onTab: (tab) => openModelTab(view.modelId, tab),
    }
    : projectModelId && takeoverReturn?.type === 'model'
      ? {
        name: projectModelName,
        tab: resolveTab(takeoverReturn.tab),
          onTab: (tab) => openModelTab(projectModelId, tab),
        withinProject: true,
      }
      : undefined;

  // The project trail (handoff step 6): Models › model › project. Earlier steps are links; each step
  // appears only once its persisted record has loaded.
  const projectTrail: TrailStep[] = [
    { label: t('nav:header.models'), onActivate: () => go(backViewFromModelHome()) },
    ...(projectModelId && projectModelName
      ? [{ label: projectModelName, onActivate: () => go({ type: 'model', modelId: projectModelId }) }]
      : []),
    ...(currentProject ? [{ label: currentProject.name }] : []),
  ];

  // Handoff step 8 (responsive). Under the theme's `sm` breakpoint (600px) the rail gives way to the
  // phone header and bottom tab bar; from 600px up the rail is the 72px docked rail at every width,
  // so 600–1100px and the desktop layout share it. The handoff gated this step on the team confirming
  // phone support; the user ruled 2026-10-01 "do them all", which waives that condition.
  const phone = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // The phone header's back: the page's parent step, the same target the trail's last link has. Top-
  // level pages (Dashboard, Models) and the terminal pages have none and show no back control.
  const lastTrailLink = [...projectTrail].reverse().find((step) => step.onActivate)?.onActivate;
  const phoneBack: (() => void) | undefined = view.type === 'model'
    ? () => go(backViewFromModelHome())
    : view.type === 'workspace'
      ? lastTrailLink
      : view.type === 'register'
        ? () => go({ type: 'models' })
        : undefined;

  // Phone tabs: inside a model (and a project under one) the bar carries the model's links, the
  // rail's model level; elsewhere it carries the two global rows.
  const phoneTabs: PhoneTab[] = modelContext
    ? visibleModelTabs().map((id) => ({
      id,
      label: t(TAB_LABEL_KEY[id]),
      Icon: TAB_ICON[id],
      active: modelContext.tab === id,
      onActivate: () => { if (modelContext.tab !== id || modelContext.withinProject) modelContext.onTab(id); },
    }))
    : NAV_ITEMS.map(({ id, labelKey, Icon }) => ({
      id,
      label: t(labelKey),
      Icon,
      active: nav === id,
      onActivate: () => go({ type: id }),
    }));

  const clearProjects = () => go({ type: 'clear' });

  return (
    <HolidayProvider firstName={identity.firstName} hideDecor={view.type === 'workspace'}>
    {/* The frame is the stacking context the holiday layers' z -1 sits in. */}
    <HolidayFrame sx={{ height: '100vh', display: 'flex', flexDirection: phone ? 'column' : 'row' }}>
      {phone ? (
        <PhoneHeader onBack={phoneBack} onClearProjects={clearProjects} />
      ) : (
      <SideRail
        projectMode={view.type === 'workspace'}
        nav={nav}
        modelContext={modelContext}
        onNavChange={(next) => {
          if (next === 'models') {
            const v: PrdView = { type: 'models' };
            setUrlView(v);
            setView(v);
          }
          if (next === 'analytics') {
            const v: PrdView = { type: 'analytics' };
            setUrlView(v);
            setView(v);
          }
        }}
        onClearProjects={clearProjects}
      />
      )}
      {/* The page column. `overflow: auto` only engages for a page taller than the viewport (the two
          terminal pages); every EnterprisePage sizes itself to this column and scrolls internally. */}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          height: phone ? undefined : '100vh',
          pb: phone ? `${PHONE_TAB_BAR_HEIGHT}px` : 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        {view.type === 'model' && (
          <ErrorBoundary fallbackMessage="Model failed to load">
            <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}>
              {/* `key` on the id so switching models REMOUNTS rather than reusing state across two
                  different subjects — the page fetches on `modelId`, and a reused instance would show
                  the previous model's fields until the new read lands. */}
              <ModelHomePage
                key={view.modelId}
                modelId={view.modelId}
                tab={view.tab}
                /* ➕ `W-14` — the hand-off, already stripped from the URL by the mount hop above.
                   It is a PROP so the page can seed it once; it is not re-readable from anywhere. */
                initialCreate={view.create}
                exportJobId={view.exportJob}
                onTabChange={(tab) => openModelTab(view.modelId, tab)}
                onModelLoaded={setModelName}
                onOpenModels={() => go(backViewFromModelHome())}
                onOpenWorkspace={(workspaceId) => {
                  // The TIER-1 takeover (`R-E`), and it reuses the SHELL's existing workspace branch
                  // rather than nesting a workspace inside the model page. §15's ruling is explicit
                  // that projects are NOT embedded panels: "clicking a project takes over the full
                  // screen, with a back button to the list — exactly today's behaviour."
                  //
                  // `originModelId` is the model we are standing on. It is stamped HERE, at the only
                  // entry point that has one, and read back by `onBack` above — the callback itself
                  // is unchanged, so `ModelHomePage` needs no new prop to supply it.
                  const next: PrdView = { type: 'workspace', workspaceId, originModelId: view.modelId };
                  setUrlView(next);
                  setView(next);
                }}
              />
            </Suspense>
          </ErrorBoundary>
        )}
        {view.type === 'models' && (
          <ErrorBoundary fallbackMessage="Models failed to load">
            <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}>
              <ModelListPage
                onOpenModel={(modelId) => {
                  const next: PrdView = { type: 'model', modelId };
                  setUrlView(next);
                  setView(next);
                }}
                onRegister={() => {
                  const next: PrdView = { type: 'register' };
                  setUrlView(next);
                  setView(next);
                }}
              />
            </Suspense>
          </ErrorBoundary>
        )}
        {view.type === 'register' && (
          <ErrorBoundary fallbackMessage="Register a model failed to load">
            <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}>
              <ModelRegisterPage
                /* PERSIST-THEN-RENDER, and the page already honours it: the id handed up here is the
                   one it READ BACK from `GET /api/models/{id}`, never the one the POST returned. So
                   landing on the homepage cannot show a model the registry does not have. */
                onRegistered={(modelId) => {
                  const next: PrdView = { type: 'model', modelId };
                  setUrlView(next);
                  setView(next);
                }}
                onCancel={() => {
                  const next: PrdView = { type: 'models' };
                  setUrlView(next);
                  setView(next);
                }}
              />
            </Suspense>
          </ErrorBoundary>
        )}
        {view.type === 'workspace' && (
          <ErrorBoundary fallbackMessage="Workspace failed to load">
            {/* W-01: same Suspense treatment as every other lazy surface in this file. The fallback
                is the shared spinner, not a bespoke one — a second loading idiom on the one screen a
                user reaches most often is how two spinners start disagreeing about what "loading"
                looks like. */}
            <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}>
            <WorkspacePage
              workspaceId={view.workspaceId}
              initialWorkspace={view.workspace}
              initialAutoOpenEnrichment={view.autoOpenEnrichment}
              onWorkspaceLoad={handleWorkspaceLoad}
              /* The wordmark goes where the app lands (the model list, ruling 2026-08-23). */
              trail={<ProjectTrailRow steps={projectTrail} onHome={() => go({ type: 'models' })} wordmark={!phone} />}
              /* ➕ B15 `W2-15` — RE-THREADED. `W2-12`'s deletion (struck below) was correct for its own
                  consumer and is NOT undone: escalation still does not navigate. This passes the prop for a
                  DIFFERENT consumer — the MDR export button, where leaving for the model IS the act. */
              onOpenModel={(modelId) => { setUrlView({ type: 'model', modelId }); setView({ type: 'model', modelId }); }}
              /* 🔴 `onOpenModel` STOOD HERE AND IS FULL-DELETED — `W2-12` (2026-09-08).
                  <s>`W-23` minted it as "the second half of escalate-to-model-log": the page POSTed the
                  escalation and then asked to be taken to the model.</s> The operator's walk found that
                  second half to be the defect — adding a finding threw the reader out of the project,
                  so adding several meant navigating back between each. `WorkspacePage` no longer
                  navigates on escalation and no longer declares the prop, so passing one here would not
                  compile.
                  ⚠️ The two OTHER `onOpenModel` handlers in this file (`ModelListPage`,
                  `DashboardPage`) are untouched — on those screens opening a model IS the action. */
            />
            </Suspense>
          </ErrorBoundary>
        )}
        {view.type === 'clear' && <ClearAllPage />}
        {view.type === 'analytics' && (
          <ErrorBoundary fallbackMessage="Dashboard failed to load">
            <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}>
              {/* The mock's ONE navigation affordance on this screen: R36, "clicking anywhere on a row
                  opens that model's homepage". Every table here targets a MODEL, never a workspace —
                  so there is deliberately no `onOpenWorkspace`, which would be a door the mock does
                  not draw. */}
              <DashboardPage
                onOpenModel={(modelId) => {
                  const next: PrdView = { type: 'model', modelId };
                  setUrlView(next);
                  setView(next);
                }}
              />
            </Suspense>
          </ErrorBoundary>
        )}
        {/* ⚠️ AMENDED 2026-08-29 — operator ruling R-Q15. Two render entries stood here and are
            FULL-DELETED with the magic-link plane: the `access-expired` terminal page, and the
            admin-only magic-link manager (`AccessGrantsDialog`), which was "mounted at top level so it
            survives view changes" with its trigger in the Settings menu's Administration section,
            gated by `isAdmin` via the `onOpenAccessGrants` prop. Neither the dialog, the settings row,
            nor the prop exists any more. */}
      </Box>
      {phone && <PhoneTabBar tabs={phoneTabs} />}
    </HolidayFrame>
    </HolidayProvider>
  );
}


